import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "material-ui/styles";
import Typography from "material-ui/Typography";
import TextField from "material-ui/TextField";

import ProgressBar from "../AccountSetting/Salesforce/common/ProgressBar";
import Button from "../Parts/Button";
import NotificationBar from "../Parts/NotificationBar";

import { validateResetPassword } from "../../utils/validator";
import {
  PasswordResetStep,
  passwordResetSteps,
} from "../../constants/ComponentTypes";
import { Link } from "react-router-dom";

const styles = () => ({
  root: {
    width: "100%",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    alignItems: "flex-start",
  },

  content: {
    flex: 1,
    width: "100%",
    overflow: "scroll",
    height: "100%",
    position: "relative",
  },

  container: {
    flex: 1,
    width: "80%",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
    maxWidth: "1200px",
    margin: "auto",
    padding: "20px",
    boxSizing: "border-box",
  },

  progressbar: {
    marginTop: "22px",
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },

  main: {
    marginTop: "100px",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
    maxWidth: "400px",
  },

  title: {
    fontSize: "24px",
    fontWeight: 800,
    marginBottom: "40px",
  },

  desc: {
    marginBottom: "20px",
    width: "100%",
    textAlign: "left",
  },

  explaination: {
    textAlign: "center",
    fontSize: "12px",
  },

  list: {
    paddingLeft: "12px",
    margin: "10px 0px",
    fontSize: "12px",
    color: "#71737B",
    "& li": {
      listStyle: "circle",
    },
  },

  form: {
    width: "100%",
  },

  buttonContainer: {
    display: "inline-flex",
    width: "100%",
    justifyContent: "center",
    margin: "40px 0",
  },

  link: {
    color: "#1A65FF",
    textDecoration: "none",
  },

  bottom: {
    padding: "10px",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
    width: "100%",
    boxSizing: "border-box",
  },
});

class ResetPassword extends React.Component {
  state = {
    step: PasswordResetStep.VERIFY_EMAIL.value,
    email: "",
    password: "",
    confirm_password: "",
    security_answer: "",
    error: {},
    showNotification: false,
    message: "",
    messageType: "success",
  };

  componentDidMount() {
    document.addEventListener("keydown", this.handleKeyDown, false);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.error !== nextProps.error && nextProps.error !== null) {
      if (
        typeof nextProps.error === "object" &&
        Object.keys(nextProps.error).length > 0
      ) {
        this.setState({
          error: {
            ...this.state.error,
            ...nextProps.error,
          },
        });
      } else if (typeof nextProps.error === "string") {
        this.displayNotification(nextProps.error, "error");
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (
      !!prevProps.isGettingSecurityQuestion &&
      !this.props.isGettingSecurityQuestion &&
      (this.props.error === null || Object.keys(this.props.error).length === 0)
    ) {
      this.setState({
        step: PasswordResetStep.RESET_PASSWORD.value,
      });
    }

    if (
      !!prevProps.isResetting &&
      !this.props.isResetting &&
      (this.props.error === null || Object.keys(this.props.error).length === 0)
    ) {
      this.setState({
        step: PasswordResetStep.SUCCESS.value,
      });
    }

    if (
      prevProps.message === "" &&
      this.props.message &&
      this.props.message !== ""
    ) {
      this.displayNotification(this.props.message, "success");
    } else if (
      prevProps.errorMessage === "" &&
      this.props.errorMessage &&
      this.props.errorMessage !== ""
    ) {
      this.displayNotification(this.props.errorMessage, "error");
    } else if (
      prevProps.shortMessage === "" &&
      this.props.shortMessage &&
      this.props.shortMessage !== ""
    ) {
      this.displayNotification(this.props.shortMessage, "green");
    }
  }

  displayNotification = (message, type = "success") => {
    this.setState({
      showNotification: true,
      message: message,
      messageType: type,
    });
  };

  handleKeyDown = (e) => {
    const enterKeyCode = 13;
    if (e.keyCode === enterKeyCode) {
      e.preventDefault();
      this.handleNext();
    }
  };

  handlePrev = () => {
    if (this.state.step === PasswordResetStep.RESET_PASSWORD.value) {
      this.setState({
        step: PasswordResetStep.VERIFY_EMAIL.value,
      });
    }
  };

  handleNext = () => {
    let error = this.validateForm();

    if (this.state.step === PasswordResetStep.VERIFY_EMAIL.value) {
      if (Object.keys(error).length === 0) {
        this.props.getPasswordSecurityQuestion({
          email: this.state.email,
          token: this.props.match.params.token,
        });
      }
    } else if (this.state.step === PasswordResetStep.RESET_PASSWORD.value) {
      let { email, password, confirm_password, security_answer } = this.state;
      if (Object.keys(error).length === 0) {
        this.props.resetPassword({
          email: email,
          password: password,
          password_confirmation: confirm_password,
          security_answer: security_answer,
          token: this.props.match.params.token,
        });
      }
    } else if (this.state.step === PasswordResetStep.SUCCESS.value) {
      this.props.goTo("/signin");
    }
  };

  handleChange = (property) => (event) => {
    let value = event.target.value;
    this.setState({
      [property]: value,
    });
  };

  validateItem = (property) => (event) => {
    let value = event.target.value;
    let err = {};

    if (property === "confirm_password") {
      err = validateResetPassword({
        password: this.state.password,
        [property]: value,
      });
    } else {
      err = validateResetPassword({
        [property]: value,
      });
    }

    let error = this.state.error;
    if (err.hasOwnProperty(property)) {
      error[property] = err[property];
    } else {
      delete error[property];
    }
    this.setState({
      error: error,
    });
  };

  validateForm = () => {
    let data = {};

    if (this.state.step === PasswordResetStep.VERIFY_EMAIL.value) {
      data = {
        email: this.state.email,
      };
    } else if (this.state.step === PasswordResetStep.RESET_PASSWORD.value) {
      data = {
        email: this.state.email,
        password: this.state.password,
        confirm_password: this.state.confirm_password,
      };

      if (this.props.security_question) {
        data["security_answer"] = this.state.security_answer;
      }
    }

    let error = validateResetPassword(data);

    this.setState({
      error: error,
    });

    return error;
  };

  renderNotification = () => {
    if (this.state.showNotification) {
      return (
        <div style={{ position: "fixed", zIndex: 1000, top: 10, width: "99%" }}>
          <NotificationBar
            message={this.state.message}
            type={this.state.messageType}
            open={this.state.showNotification}
            onHandleClose={() => {
              this.setState({
                showNotification: false,
              });
            }}
            autoClose={true}
            style={{
              width:
                this.state.messageType === "green" ? "fit-content" : "100%",
            }}
          ></NotificationBar>
        </div>
      );
    }
  };

  renderDesc = () => {
    const { classes } = this.props;

    if (this.state.step === PasswordResetStep.VERIFY_EMAIL.value) {
      return (
        <div className={classes.desc}>
          <Typography variant="caption" className={classes.explaination}>
            リセットしたいユーザのメールアドレスを入力してください
          </Typography>
        </div>
      );
    } else if (this.state.step === PasswordResetStep.RESET_PASSWORD.value) {
      return (
        <div className={classes.desc}>
          <Typography
            variant="caption"
            className={classes.explaination}
            style={{ textAlign: "left" }}
          >
            {this.state.email}の新しいパスワードを入力してください。
            少なくとも次のものを含めてください。
          </Typography>
          <ul className={classes.list}>
            <li>８文字以上</li>
            <li>１個以上の半角英大文字</li>
            <li>１個以上の半角英小文字</li>
            <li>１個以上の半角数字</li>
          </ul>
        </div>
      );
    } else if (this.state.step === PasswordResetStep.SUCCESS.value) {
      return (
        <div className={classes.desc}>
          <Typography variant="caption" className={classes.explaination}>
            パスワードがリセットされました。
          </Typography>
        </div>
      );
    }
  };

  renderEmailForm = () => {
    const { classes } = this.props;
    const { error } = this.state;

    return (
      <form className={classes.form}>
        <TextField
          value={this.state.email}
          id="email"
          label="メールアドレス"
          type="text"
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            disableUnderline: true,
          }}
          placeholder="メールアドレスを入力"
          onChange={this.handleChange("email")}
          onBlur={this.validateItem("email")}
          onKeyDown={this.handleKeyDown}
          error={error.hasOwnProperty("email")}
          helperText={error.email}
          margin="normal"
          fullWidth
        />
      </form>
    );
  };

  renderSecurityQuestionForm = () => {
    const { classes, security_question } = this.props;
    const { error } = this.state;

    return (
      <form className={classes.form}>
        {!!this.props.security_question && (
          <TextField
            value={this.state.security_answer}
            id="security_answer"
            label={security_question}
            type="text"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              disableUnderline: true,
            }}
            placeholder="セキュリティ質問の回答を入力"
            onChange={this.handleChange("security_answer")}
            onBlur={this.validateItem("security_answer")}
            onKeyDown={this.handleKeyDown}
            error={error.hasOwnProperty("security_answer")}
            helperText={error.security_answer}
            margin="normal"
            fullWidth
          />
        )}

        <TextField
          value={this.state.password}
          id="password"
          label="新しいパスワード"
          type="password"
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            disableUnderline: true,
          }}
          placeholder="新しいパスワードを入力"
          onChange={this.handleChange("password")}
          onBlur={this.validateItem("password")}
          onKeyDown={this.handleKeyDown}
          error={error.hasOwnProperty("password")}
          helperText={error.password}
          margin="normal"
          fullWidth
        />
        <TextField
          value={this.state.confirm_password}
          id="confirm-password"
          label="新しいパスワード（確認）"
          type="password"
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            disableUnderline: true,
          }}
          placeholder="確認のためにもう一度新しいパスワードを入力"
          onChange={this.handleChange("confirm_password")}
          onBlur={this.validateItem("confirm_password")}
          onKeyDown={this.handleKeyDown}
          error={error.hasOwnProperty("confirm_password")}
          helperText={error.confirm_password}
          margin="normal"
          fullWidth
        />
      </form>
    );
  };

  renderForm = () => {
    switch (this.state.step) {
      case PasswordResetStep.VERIFY_EMAIL.value:
        return this.renderEmailForm();
      case PasswordResetStep.RESET_PASSWORD.value:
        return this.renderSecurityQuestionForm();
      default:
        return "";
    }
  };

  render() {
    const { classes } = this.props;
    const { step } = this.state;

    return (
      <div className={classes.root}>
        <div className={classes.content}>
          <div className={classes.container}>
            <div className={classes.progressbar}>
              <ProgressBar step={this.state.step} steps={passwordResetSteps} />
            </div>

            <div className={classes.main}>
              <Typography variant="title" className={classes.title}>
                パスワードをリセット
              </Typography>

              {this.renderDesc()}
              {this.renderForm()}

              <div className={classes.buttonContainer}>
                {step === PasswordResetStep.RESET_PASSWORD.value && (
                  <Button
                    text="戻る"
                    type="line"
                    size="medium"
                    handleClickButton={this.handlePrev}
                    style={{ marginRight: "20px" }}
                    onKeyDown={(e) => {
                      e.preventDefault();
                    }}
                  ></Button>
                )}
                <Button
                  text={(() => {
                    switch (step) {
                      case PasswordResetStep.VERIFY_EMAIL.value:
                        return "次へ";
                      case PasswordResetStep.RESET_PASSWORD.value:
                        return "パスワードをリセット";
                      case PasswordResetStep.SUCCESS.value:
                        return "サインイン";
                      default:
                        return "次へ";
                    }
                  })()}
                  size="medium"
                  loading={
                    this.props.isGettingSecurityQuestion ||
                    this.props.isResetting
                  }
                  handleClickButton={this.handleNext}
                  onKeyDown={this.handleKeyDown}
                ></Button>
              </div>
              <div className={classes.bottom}>
                <Typography variant="body2" style={{ color: "#71737B" }}>
                  <Link to="/" className={classes.link}>
                    トップ
                  </Link>
                </Typography>
              </div>
            </div>
          </div>
        </div>
        {this.renderNotification()}
      </div>
    );
  }
}

ResetPassword.propTypes = {
  classes: PropTypes.object.isRequired,
  error: PropTypes.object.isRequired,
  message: PropTypes.string,
  errorMessage: PropTypes.string,
  shortMessage: PropTypes.string,
  isGettingSecurityQuestion: PropTypes.bool,
  isResetting: PropTypes.bool,
  security_question: PropTypes.string,
  getPasswordSecurityQuestion: PropTypes.func,
  resetPassword: PropTypes.func,
  goTo: PropTypes.func,
  match: PropTypes.object,
};

export default withStyles(styles)(ResetPassword);



// WEBPACK FOOTER //
// ./src/components/ResetPassword/ResetPassword.jsx